<script setup lang="ts">
import { onMounted, ref } from 'vue';

// Imported from the module rather than the composables barrel: that barrel imports back
// into the design system, and the cycle leaves the re-exports uninitialised.
import { useScannerInput, SCANNER_INPUT_ATTRIBUTE } from '@/composables/useScannerInput';
import { trimScan } from '@/utils/scannerKeyboardLayout';
import type { ScannerScanType } from '@/utils/scannerKeyboardLayout';
import BTextField from './BTextField.vue';
import BIcon from './BIcon.vue';

type PropsType = {
    modelValue?: string;
    label?: string;
    icon?: string;
};

const props = withDefaults(defineProps<PropsType>(), {
    modelValue: '',
    icon: 'mdi-qrcode-scan',
});

const emit = defineEmits<{
    'update:modelValue': [value: string];

    /**
     * The scanned burst. `keys` carries the physical key presses behind it so the consumer can
     * re-read a mangled code; it is empty for a value that was typed or pasted.
     */
    scan: [scan: ScannerScanType];
}>();

defineOptions({ inheritAttrs: false });

/**
 * A burst coming from a barcode scanner is rebuilt from the physical keys, so the value is
 * correct even when the operating system runs a non-US keyboard layout; anything typed or
 * pasted is emitted as entered.
 */
const { handleKeydown, takeScan, takeBurst } = useScannerInput({
    // Plenty of scanners send no Enter suffix, so the burst also closes on its own.
    onBurstEnd: burst => {
        if (burst.isLongEnough && burst.isFast) {
            emit('scan', trimScan(burst));
        }
    },
});

const textFieldRef = ref<InstanceType<typeof BTextField> | null>(null);

const emitScan = (): void => {
    emit('scan', trimScan(takeScan(props.modelValue)));
};

/**
 * Some scanners suffix a code with Tab rather than Enter. Its default action moves the focus
 * off the field, so the burst has to be closed on keydown, before that happens, and the key
 * swallowed.
 *
 * The burst is read first and the key is only swallowed once it turns out to be machine
 * input: the shadow buffer fills up on anything typed too, so deciding on "a buffer exists"
 * would trap the focus of anyone who types a code by hand and tabs on.
 */
const handleScannerKeydown = (event: KeyboardEvent): void => {
    handleKeydown(event);

    if (event.key !== 'Tab') {
        return;
    }

    const burst = takeBurst();

    if (!burst.isLongEnough || !burst.isFast) {
        return;
    }

    event.preventDefault();
    emit('scan', trimScan(burst));
};

const focus = (): void => {
    const input = textFieldRef.value?.$el?.querySelector('input');

    input?.focus();
};

onMounted(() => {
    // Marks the field as self-handling for useGlobalScannerInput.
    textFieldRef.value?.$el?.querySelector('input')?.setAttribute(SCANNER_INPUT_ATTRIBUTE, '');
});

defineExpose({ focus });
</script>

<template>
    <BTextField
        ref="textFieldRef"
        :model-value="modelValue"
        :label="label"
        v-bind="$attrs"
        @update:model-value="emit('update:modelValue', String($event))"
        @keydown="handleScannerKeydown"
        @keyup.enter="emitScan"
    >
        <template #prepend-inner>
            <BIcon
                :icon="icon"
                size="small"
                class="opacity-50"
            />
        </template>
    </BTextField>
</template>
